import React from 'react';

/**
 * Función para renderizar texto enriquecido preservando saltos de línea y emojis
 * @param text - El texto a renderizar
 * @returns Elementos JSX con el texto formateado
 */
export const renderRichText = (text: string): React.ReactNode => {
  if (!text) return null;
  
  const anchorTagRegex = /<a\s+[^>]*href\s*=\s*["']([^"']+)["'][^>]*>(.*?)<\/a>/gi;
  
  return text.split('\n').map((line, lineIndex) => {
    // Handle existing <a> tags
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = anchorTagRegex.exec(line)) !== null) {
      // Add text before the <a> tag
      if (match.index > lastIndex) {
        const beforeText = line.slice(lastIndex, match.index);
        parts.push({ type: 'text', content: beforeText });
      }
      
      // Add the <a> tag
      parts.push({
        type: 'anchor',
        href: match[1],
        content: match[2]
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text after the last <a> tag
    if (lastIndex < line.length) {
      parts.push({ type: 'text', content: line.slice(lastIndex) });
    }
    
    // If no <a> tags were found, treat the whole line as text
    if (parts.length === 0) {
      parts.push({ type: 'text', content: line });
    }
    
    return (
      <React.Fragment key={lineIndex}>
        {parts.map((part, partIndex) => {
          if (part.type === 'anchor') {
            return (
              <a 
                key={partIndex} 
                href={part.href} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                {part.content}
              </a>
            );
          } else {
            return part.content;
          }
        })}
        {lineIndex < text.split('\n').length - 1 && <br />}
      </React.Fragment>
    );
  });
};

/**
 * Función para renderizar texto enriquecido con estilos personalizados
 * @param text - El texto a renderizar
 * @param className - Clases CSS adicionales
 * @param style - Estilos inline adicionales
 * @returns Elemento JSX con el texto formateado y estilos
 */
export const renderRichTextWithStyles = (
  text: string, 
  className?: string, 
  style?: React.CSSProperties
): React.ReactNode => {
  if (!text) return null;
  
  return (
    <span className={className} style={style}>
      {renderRichText(text)}
    </span>
  );
}; 